/**
 * Dev-only: remember the last visited route in sessionStorage and restore it
 * when the app boots at "/". Compiled out in production by `import.meta.env.DEV`.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { sandboxStorageKey } from '@/auth/sandboxSelection';

const STORAGE_KEY = sandboxStorageKey('dt:dev:lastRoute');

// /callback receives Auth0 tokens in the URL hash (response_type: 'token id_token').
// Persisting it would leak tokens into sessionStorage and could route a later mount
// back through a stale callback URL.
const SKIP_PATHS = new Set(['/callback']);

const safeGetItem = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage disabled or quota exceeded — silently no-op.
  }
};

export const useDevRoutePersistence = (): void => {
  const location = useLocation();
  const navigate = useNavigate();
  const restoredRef = useRef(false);

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    if (!restoredRef.current) {
      restoredRef.current = true;
      const saved = safeGetItem(STORAGE_KEY);
      if (saved && saved !== '/' && location.pathname === '/') {
        navigate(saved, { replace: true });
        return;
      }
    }

    if (SKIP_PATHS.has(location.pathname)) return;

    safeSetItem(STORAGE_KEY, location.pathname + location.search);
  }, [location, navigate]);
};
